// docs/51 friendly villages: the screen a band walks into from the chart. The street (and the inn,
// the shop, the smithy, the temple) is `village/VillageView`; over it this screen puts the top bar
// (the village, who pays, the way back to the chart), a cry for help when the village's trouble has
// broken out, the talk (a dialogue box with the villager's branching topics, their deal, a round at
// the inn, a secret for coin), the counter of the shop or smithy, and what the player has heard
// here. Every word is the storyteller's, from the `village` / `village_npc` rows; secrets and
// rounds come back through `my_village_known` once paid for. docs/64: the band walks over to whoever
// it talks to, prices show before anyone asks, a keeper opens the counter (buy, sell, what each piece
// does), and the innkeeper sits a band down to a meal that leaves it well fed for its next fight.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Button, ButtonRow, DialogueBox, HeroSprite, OryxIcon, Panel, Select } from '../components';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useHeroCallings, useHeroMeals, useHeroPacks, useMyInventory, useMyHeroes, useMyVillageKnown, useOffers, useParties, useVillageFigures, useVillageNpcs, useVillagePresence, useVillageSeats, useVillages, useVillageStock, useVillageWorkshops, useItemDyes, useItemUpgrades, useUpgradesOf, useWorld, useWorldClock, useWorldHeroes, useWorldPartyMembers } from '../../core/subscriptions/hooks';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex, tsToMs } from '../../core/net/connection';
import { PACKS } from '../../core/sim/gear';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { OFFER } from '../../core/adventure/graph';
import { playSfx } from '../../core/audio';
import { townBand } from '../../core/audio/places';
import { seasonKey } from '../../core/story/calendar';
import { parseOwWeather } from './map/worldSky';
import { seedFrom } from './map/overworld';
import { useAcceptance } from './World';
import { sellValue } from '../../core/sim/gear';
import { VillageView } from './village/VillageView';
import { alwaysStocked, Counter, type CounterItem, type CounterPack } from './village/Counter';
import { TownBand } from './village/Band';
import { openHeroSheet } from '../heroes/heroSheetStore';
import { getBridge } from '../../core/encounter/session';
import { COMPANION_KINDS, coatFrame, companionIdOf, isCompanionPick } from '../../core/sim/companion';
import { isWorkshop, SPOT_ORDER, type BuildingKind, type DealKind, type MealStage, type VillageHero, type VillageLook, type VillageMeal, type VillageNpc as ViewNpc, type VillageSpot, type VillageStand, type VillageSummon, type HeldSeat, type RemoteFigures } from './village/types';
import { Workshop, type WorkshopView } from './village/Workshop';
import { Dyer } from './village/Dyer';
import { useHeroLooks } from '../../core/heroes/looks';
import { shopKey, SHOP_TITLES } from '../../core/items/upgrades';
import { isHotkey } from '../../core/input/hotkeys';
import { useInnData } from './village/inn/useInnData';
import './village/talk.css';

/** `village_presence.spot` order (`town.rs` SPOTS): the module's `Building`s, then the docs/112 workshops. */
const SPOTS: readonly VillageSpot[] = SPOT_ORDER;
/** docs/112: a workshop keeper's key in the view (they are no `village_npc`; they keep the counter). */
const WS_KEY = 'ws-';
const KEEPER_EPITHET: Record<string, string> = {
  enchanter: 'the enchanter', hedgewitch: 'the hedge-witch', runecarver: 'the runecarver', stargazer: 'the stargazer',
  weaponsmith: 'the bladewright', armourer: 'the armourer', bowyer: 'the bowyer and fletcher', leatherworker: 'the tanner and tailor',
  dyer: 'the dyer',
};
const DEALS: Array<DealKind | undefined> = [undefined, 'hire', 'secret', 'trade'];
const SIZES = ['hamlet', 'village', 'town'] as const;
/** fk_forge::village: `hire_price(level) = 40 + 20·level`, `ROUND_PRICE`. */
export const hirePrice = (level: number) => 40 + 20 * Math.max(1, level);
export const ROUND_PRICE = 6;
/** fk_forge::village `MEAL_PRICE`: a meal at the inn, for each hero at the table (docs/64 VP4). */
export const MEAL_PRICE = 4;
const ROLE_INNKEEPER = 0, ROLE_MERCHANT = 1, ROLE_SMITH = 2, ROLE_PRIEST = 3;
/** docs/72: every building with a counter — the shop and smithy, and the inn (food) and temple (holy and healing pieces). */
const COUNTER_SPOTS: readonly VillageSpot[] = ['shop', 'smithy', 'inn', 'temple'];
const KEEPER_AT: Partial<Record<VillageSpot, number>> = { shop: ROLE_MERCHANT, smithy: ROLE_SMITH, inn: ROLE_INNKEEPER, temple: ROLE_PRIEST };
const NPC_JOINED = 1, NPC_SOLD = 2;
/** Screens looked at from town without leaving it (back to the village after). */
const STAYS_IN_TOWN: readonly string[] = ['village', 'inventory', 'roster', 'levelup', 'ledger', 'settings', 'realm'];
const KNOWN_ROUND = 1;

type Npc = ReturnType<typeof useVillageNpcs>[number];
type Hero = ReturnType<typeof useMyHeroes>[number];

/** Where a conversation stands: a greeting, a topic (or its follow-up), a deal, a secret, a round, the trouble. */
type Talk =
  | { at: 'greet' }
  | { at: 'topic'; i: number; follow: boolean }
  | { at: 'deal' }
  | { at: 'said'; line: string }
  | { at: 'secret' }
  | { at: 'trouble' }
  | { at: 'bye' };

/** The talk's line for where it stands. */
function lineOf(n: Npc, t: Talk, troublePitch: string): string {
  switch (t.at) {
    case 'greet': return n.greeting || '…';
    case 'topic': { const q = n.topics[t.i]; return (t.follow ? q?.followAnswer : q?.answer) ?? '…'; }
    case 'deal': return n.dealPitch || '…';
    case 'said': return t.line;
    case 'secret': return n.secretTeaser || '…';
    case 'trouble': return troublePitch;
    case 'bye': return n.farewell || 'Farewell.';
  }
}

const isEnchantShop = (w: WorkshopView): boolean => ['enchanter', 'hedgewitch', 'runecarver', 'stargazer', 'temple'].includes(w.shop);
/** `item.improve`'s bands (sfx.banks.json): metal work at the forges, the bowyer's string and fletching, the tanner's stitching. */
const IMPROVE_BAND: Partial<Record<WorkshopView['shop'], number>> = { weaponsmith: 0, armourer: 0, smithy: 0, bowyer: 1, leatherworker: 2 };

export function VillageScreen() {
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const go = useRoute((s) => s.go);
  const villageId = useRoute((s) => s.villageId);
  const world = useWorld(worldId);
  const clock = useWorldClock(worldId);
  // a row update can drop the village for a render; keep the last one seen, or the whole view remounts (and a meal starts over)
  const vNow = useVillages(worldId).find((x) => x.id === villageId);
  const vLast = useRef(vNow);
  if (vNow) vLast.current = vNow;
  const v = vNow ?? (vLast.current?.id === villageId ? vLast.current : undefined);
  const allNpcs = useVillageNpcs(worldId);
  const [talkingId, setTalkingId] = useState<bigint | undefined>();
  // a hireling who has just joined stays to say so, and walks off when the talk ends
  const npcs = useMemo(() => allNpcs.filter((n) => n.villageId === villageId && (n.state !== NPC_JOINED || n.id === talkingId)).sort((a, b) => a.slot - b.slot), [allNpcs, villageId, talkingId]);
  const stock = useVillageStock(worldId).filter((s) => s.villageId === villageId);
  // docs/112 UP6: this village's workshops (its own buildings) and the smithy's and temple's upgrade counters
  const allWorkshops = useVillageWorkshops(worldId);
  const workshops = useMemo<WorkshopView[]>(() => allWorkshops.filter((w) => w.villageId === villageId).sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((w) => ({ id: w.id, shop: shopKey(w.shop), keeper: w.keeper, offers: [...w.offers], frame: w.frame })), [allWorkshops, villageId]);
  const [workshopOpen, setWorkshopOpen] = useState<bigint>();
  const upsOf = useUpgradesOf(worldId);
  const upRows = useItemUpgrades(worldId);
  const spentOf = (id: bigint): number => upRows.find((u) => u.itemId === id)?.spent ?? 0;
  const known = useMyVillageKnown();
  const offers = useOffers(worldId);
  const mine = useMyHeroes().filter((h) => h.worldId === worldId && h.status !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
  const parties = useParties(worldId);
  const worldHeroes = useWorldHeroes(worldId);
  const members = useWorldPartyMembers(worldId);
  const push = useToasts((s) => s.push);
  const { party, reason } = useAcceptance(worldId ?? 0n);
  const callings = useHeroCallings(worldId);
  const meals = useHeroMeals(worldId);
  const inventory = useMyInventory();
  // docs/142: every hero's look (their dye among it), for the dyer's counter
  const looks = useHeroLooks((st) => st.looks);
  // docs/156: each piece's own dye (no row: a worn piece is in its wearer's, a packed one as drawn)
  const itemDyes = useItemDyes(worldId);
  const ownDye = useMemo(() => new Map(itemDyes.map((d) => [d.itemId.toString(), d.dye])), [itemDyes]);
  const packs = useHeroPacks(worldId);
  const presence = useVillagePresence(worldId);
  const figureRows = useVillageFigures(worldId);
  const seatRows = useVillageSeats(worldId);

  const [spot, setSpotRaw] = useState<VillageSpot>('street');
  // docs/120 VM5: the room I last came out of (my band steps out of its door)
  const [cameFrom, setCameFrom] = useState<VillageSpot>();
  const setSpotHere = (k: VillageSpot) => { if (k !== spot) setCameFrom(spot); setSpotRaw(k); };
  const [talk, setTalk] = useState<Talk>({ at: 'greet' });
  const [payerId, setPayerId] = useState<bigint | undefined>();
  const [busy, setBusy] = useState<string | undefined>();
  const [heard, setHeard] = useState(false);
  // docs/64: the band walks to whoever it is sent to; the counter opens on asking; the meal at the inn
  const [summon, setSummon] = useState<VillageSummon>();
  const callOver = (id: bigint | string) => setSummon((s) => ({ key: id.toString(), n: (s?.n ?? 0) + 1 }));
  const [counterOpen, setCounterOpen] = useState(false);
  // "Ask to see the wares": the band walks to the keeper and the counter opens on arrival
  const toCounter = useRef<string | undefined>(undefined);
  const [meal, setMeal] = useState<VillageMeal>();
  const [mealStage, setMealStage] = useState<MealStage>();
  // a band that reloads straight into the village still walks in (stock, talk, the week's trouble)
  useEffect(() => { if (villageId !== undefined) void rpc.enterVillage(villageId); }, [villageId]);
  useEffect(() => { if (!villageId) go('world'); }, [villageId, go]);

  // docs/73 TP1: where I am in the village is my `village_presence` row. docs/120 VM1: I go through the doors
  // myself; only a quest I come along on (VM6) moves me, out to the chart
  const myRow = presence.find((p) => identityHex(p.owner) === me);
  const setSpot = (k: VillageSpot) => { setSpotHere(k); if (villageId !== undefined) void rpc.villageGo(villageId, SPOTS.indexOf(k)); };
  const resetScene = () => { setTalkingId(undefined); setTalk({ at: 'greet' }); setCounterOpen(false); setWorkshopOpen(undefined); setMeal(undefined); setMealStage(undefined); };
  const rowSig = myRow ? `${myRow.villageId}:${myRow.spot}:${tsToMs(myRow.at)}` : '';
  useEffect(() => {
    if (!myRow || identityHex(myRow.movedBy) === me) return;
    // docs/120 VM6: a muster I came along on (or called) has set out; the route opens the quest's map
    if (myRow.villageId === 0n) { if (!members.some((m) => identityHex(m.owner) === me && parties.some((p) => p.id === m.partyId && p.state === 1))) go('world'); return; }
    if (myRow.villageId !== villageId) { resetScene(); setSpotHere('street'); go('village', { villageId: myRow.villageId }); return; }
    const to = SPOTS[myRow.spot] ?? 'street';
    if (to !== spot) { resetScene(); setSpotHere(to); }
  }, [rowSig]);
  // a dropped connection forgets where I stood: say so again once it is back
  useEffect(() => {
    if (myRow || villageId === undefined) return;
    const t = window.setTimeout(() => void rpc.enterVillage(villageId).then((r) => { if (r.ok && spot !== 'street') void rpc.villageGo(villageId, SPOTS.indexOf(spot)); }), 2500);
    return () => window.clearTimeout(t);
  }, [!!myRow, villageId]);
  // walking off to the chart (or a fight, a quest) leaves the village; docs/120 VM3: my band-mates still here
  // are asked whether to come. A look at a hero's sheet, the inventory or the settings is not leaving town.
  useEffect(() => () => { if (worldId !== undefined && !STAYS_IN_TOWN.includes(useRoute.getState().screen)) void rpc.leaveVillage(worldId); }, [worldId]);
  const onStand = (st: VillageStand) => { if (villageId !== undefined) void rpc.villageStand(villageId, SPOTS.indexOf(spot), st.x, st.y, st.pose); };

  // who pays: the chosen hero, else the richest at home
  const payable = mine.filter((h) => h.status !== 1);
  const payer: Hero | undefined = payable.find((h) => h.id === payerId) ?? [...payable].sort((a, b) => b.gold - a.gold)[0];
  const gold = mine.reduce((s, h) => s + h.gold, 0);
  const level = mine.length ? Math.max(1, Math.floor(mine.reduce((s, h) => s + h.level, 0) / mine.length)) : 1;

  // a player's heroes in town: those in the band they stand in, else all theirs at home
  const bandOf = (owner: string) => parties.find((p) => p.state !== 2 && members.some((m) => m.partyId === p.id && identityHex(m.owner) === owner));
  const inTown = (owner: string, hs: typeof worldHeroes) => { const b = bandOf(owner); const inBand = b ? hs.filter((h) => members.some((m) => m.partyId === b.id && m.heroId === h.id)) : []; return inBand.length ? inBand : hs; };
  // docs/73 TP3: a villager just taken on steps out from where they stood once the talk is over
  const hiredFrom = (heroId: bigint) => allNpcs.find((n) => n.villageId === villageId && n.state === NPC_JOINED && n.joinedHero === heroId);
  const myBand = bandOf(me);
  const leading = !!myBand && identityHex(myBand.lead) === me && members.some((m) => m.partyId === myBand.id && identityHex(m.owner) !== me);
  const bandHeroes = inTown(me, mine).filter((h) => talkingId === undefined || hiredFrom(h.id)?.id !== talkingId);
  // docs/73 TP2: every other player in this room of this village, in my band or not (their `village_presence` row)
  const there = presence.filter((p) => identityHex(p.owner) !== me && p.villageId === villageId && (SPOTS[p.spot] ?? 'street') === spot).sort((a, b) => (a.id < b.id ? -1 : 1));
  // docs/120 VM2: each of them with the heroes their own view draws (their `village_figures` row), else
  // those of their band, else all theirs at home (up to four)
  const spotN = SPOTS.indexOf(spot);
  const figsOf = (owner: string) => figureRows.find((f) => identityHex(f.owner) === owner && f.villageId === villageId && f.spot === spotN);
  const others = there.flatMap((p) => {
    const owner = identityHex(p.owner);
    const stand: VillageStand = { x: p.x, y: p.y, pose: p.pose };
    const theirs = worldHeroes.filter((h) => identityHex(h.owner) === owner && h.status === 0).sort((a, b) => (a.id < b.id ? -1 : 1));
    const told = figsOf(owner)?.figures.filter((f) => !f.beast).map((f) => f.heroId);
    const drawn = told?.length ? theirs.filter((h) => told.includes(h.id)) : inTown(owner, theirs).slice(0, 4);
    return drawn.map((h) => ({ h, owner, stand }));
  });
  const remote: RemoteFigures[] = there.flatMap((p) => {
    const f = figsOf(identityHex(p.owner));
    return f ? [{ owner: identityHex(p.owner), atMs: tsToMs(f.at) ?? 0, figures: f.figures, ...(f.talkNpc ? { talkNpc: f.talkNpc.toString() } : {}) }] : [];
  });
  const held: HeldSeat[] = seatRows.filter((r) => r.villageId === villageId && r.spot === spotN).map((r) => ({ seat: r.seat, owner: identityHex(r.owner), heroId: r.heroId }));
  // docs/64 VP2: a ranger's beast walks at its ranger's heel (docs/59 companions)
  const beasts = getBridge()?.callings()?.companions ?? [];
  const heroes: VillageHero[] = [...bandHeroes.slice(0, 6).map((h) => ({ h, owner: me, stand: undefined as VillageStand | undefined })), ...others].flatMap(({ h, owner, stand }) => {
    const own = owner === me;
    const from = own ? hiredFrom(h.id) : undefined;
    const hero: VillageHero = { key: h.id.toString(), name: h.name, frame: h.oryxFrame, classId: CLASS_NAMES[h.class] ?? 'fighter', id: h.id, mine: own, owner, ...(stand ? { stand } : {}), ...(from ? { from: from.id.toString() } : {}) };
    const c = callings.find((x) => x.heroId === h.id);
    const beast = c && isCompanionPick(c.pick) ? beasts.find((b) => b.calling === c.pick) : undefined;
    return beast ? [hero, { key: `${h.id}-beast`, name: c!.petName || COMPANION_KINDS[c!.pick] || 'Companion', frame: coatFrame(beast, c!.petCoat), classId: 'companion', mine: own, owner, ...(stand ? { stand } : {}), companion: true, petId: BigInt(companionIdOf(Number(h.id))) }] : [hero];
  });
  const myHeroes = heroes.filter((h) => h.mine && !h.companion);
  // docs/116 the cozy inn: its rows, and what can be asked for there (the inn and the room upstairs)
  const inInn = spot === 'inn' || spot === 'upstairs';
  const innKeys = useMemo(() => new Map(heroes.filter((h) => h.id !== undefined && !h.companion).map((h) => [h.id!, h.key])), [heroes.map((h) => h.key).join()]);
  const innPayers = payable.filter((h) => myHeroes.some((x) => x.id === h.id));
  // docs/121 VW5: a side quest taken up from the noticeboard musters the band as a village's trouble does (docs/120 VM6)
  const takeUp = async (offerId: bigint): Promise<boolean> => {
    const o = offers.find((x) => x.id === offerId);
    const askedAlong = members.some((m) => myBand && m.partyId === myBand.id && identityHex(m.owner) !== me);
    const r = await rpc.takeUpOffer(offerId);
    if (r.ok) { playSfx('inn.cheers', { gain: 0.7 }); push(askedAlong ? `You take up “${o?.title ?? 'the quest'}” and call your band-mates to come.` : `You take up “${o?.title ?? 'the quest'}”.`, 'success'); }
    return r.ok;
  };
  const inn = useInnData({ worldId, villageId, me, on: inInn, payers: innPayers, heroKeys: innKeys, goUpstairs: (up) => { resetScene(); setSpot(up ? 'upstairs' : 'inn'); }, takeUp });

  const look: VillageLook | undefined = useMemo(() => !v ? undefined : ({
    seed: (seedFrom(world?.worldSeed, world?.id) ^ Math.imul(v.index + 1, 0x9e3779b1)) >>> 0,
    name: v.name,
    size: SIZES[v.size] ?? 'village',
    buildings: [
      ...([['inn', v.inn], ['shop', v.shop], ['smithy', v.smithy], ['temple', v.temple]] as Array<[BuildingKind, string]>).filter(([, name]) => !!name).map(([kind, name]) => ({ kind, name })),
      // docs/112 UP6: the workshops stand along the street after the village's own buildings
      ...workshops.filter((w) => isWorkshop(w.shop as VillageSpot)).map((w) => ({ kind: w.shop as BuildingKind, name: w.keeper || SHOP_TITLES[w.shop] })),
    ],
    biome: clock?.biome,
    season: seasonKey(clock?.season),
    weather: parseOwWeather(clock?.weather),
    night: townBand(clock?.weekIndex) === 1,
    haven: v.index === 0,
  }), [v?.id, v?.name, v?.inn, v?.shop, v?.smithy, v?.temple, v?.size, world?.id, clock?.biome, clock?.season, clock?.weather, clock?.weekIndex, workshops.map((w) => w.shop).join()]);

  const alarmOffer = v && v.alarmOffer ? offers.find((o) => o.id === v.alarmOffer && o.state === OFFER.OPEN) : undefined;
  const viewNpcs: ViewNpc[] = npcs.map((n) => ({
    key: n.id.toString(), name: n.name, epithet: n.epithet, frame: n.frame, at: SPOTS[n.building] ?? 'street',
    deal: n.state === NPC_SOLD ? undefined : DEALS[n.deal],
    quest: n.topics.some((t) => !!t.about) || !!n.secretAbout,
    alarm: !!alarmOffer && v?.alarmNpc === n.id,
    ...(n.state === NPC_SOLD ? {} : DEALS[n.deal] === 'hire' ? { price: hirePrice(level) } : DEALS[n.deal] ? { price: n.price } : {}),
  })).concat(workshops.filter((w) => isWorkshop(w.shop as VillageSpot)).map((w) => ({
    // docs/112: each workshop's keeper stands at their post inside
    key: `${WS_KEY}${w.id}`, name: w.keeper, epithet: KEEPER_EPITHET[w.shop] ?? 'the keeper', frame: w.frame ?? 79, at: w.shop as VillageSpot,
    deal: undefined, quest: false, alarm: false,
  })));

  const talking = npcs.find((n) => n.id === talkingId);
  const startTalk = (key: string) => {
    // docs/112: a workshop's keeper opens their counter
    if (key.startsWith(WS_KEY)) {
      toCounter.current = undefined;
      setTalkingId(undefined); setCounterOpen(false);
      setWorkshopOpen(BigInt(key.slice(WS_KEY.length))); playSfx('ui.open');
      return;
    }
    const id = BigInt(key);
    const wares = toCounter.current === key;
    toCounter.current = undefined;
    if (wares) { setCounterOpen(true); playSfx('ui.open'); return; }
    setTalkingId(id);
    setTalk(alarmOffer && v?.alarmNpc === id ? { at: 'trouble' } : { at: 'greet' });
    setCounterOpen(false);
    playSfx('ui.chat');
  };
  const endTalk = () => { setTalkingId(undefined); setTalk({ at: 'greet' }); };
  useEffect(() => {
    // a dialog over the talk (the inventory, a hero's card) takes its own Esc
    const onKey = (e: KeyboardEvent) => { if (talkingId !== undefined && isHotkey(e, 'talk.leave') && !document.querySelector('[aria-modal="true"]')) { e.stopPropagation(); endTalk(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [talkingId]);

  // what I have paid to hear in this village, newest first
  const mineHere = useMemo(() => known.filter((k) => allNpcs.some((n) => n.id === k.npcId && n.villageId === villageId)).sort((a, b) => (a.id < b.id ? 1 : -1)), [known, allNpcs, villageId]);
  // a secret just bought or a round just stood: say it at once
  useEffect(() => {
    if (!heard || !talking) return;
    const latest = mineHere[0];
    if (latest && latest.npcId === talking.id) { setTalk({ at: 'said', line: latest.text }); setHeard(false); }
  }, [heard, mineHere[0]?.id]);

  // docs/64 VP4: once the plates are clean the meal counts; the module checks it has been long enough.
  // The retries stop when the screen goes, so no toast or sound lands on another screen.
  const mealRetry = useRef<{ live: boolean; timer?: number }>({ live: true });
  useEffect(() => {
    const m = mealRetry.current;
    m.live = true;
    return () => { m.live = false; window.clearTimeout(m.timer); };
  }, []);
  const onMealStage = (st: MealStage) => {
    setMealStage(st);
    if (st !== 'resting' || !v) return;
    const m = mealRetry.current;
    window.clearTimeout(m.timer);
    const finish = (tries: number) => void rpc.finishMeal(v.id).then((r) => {
      if (!m.live) return;
      if (r.ok) { playSfx('item.buff', { gain: 0.7 }); push('Well fed: +3 max HP in the next fight.', 'success'); }
      else if (tries > 0) m.timer = window.setTimeout(() => finish(tries - 1), 1500);
    });
    finish(6);
  };

  if (!worldId || !v || !look) return <main className="fk-village-screen" data-testid="village-screen" data-ready="false" />;

  const toldSecret = (n: Npc) => known.find((k) => k.npcId === n.id && k.kind === 0);

  const pay = async (key: string, price: number, fn: (heroId: bigint) => Promise<{ ok: boolean }>): Promise<boolean> => {
    if (!payer) { push('None of your heroes is at home to pay.', 'error'); return false; }
    if (payer.gold < price) { push(`${payer.name} has ${payer.gold} gold; that costs ${price}.`, 'error'); return false; }
    setBusy(key);
    const r = await fn(payer.id);
    setBusy(undefined);
    if (r.ok) playSfx(price >= 100 ? 'ui.coins_big' : 'ui.coins');
    return r.ok;
  };

  const choices: ReactNode[] = [];
  if (talking) {
    const n = talking;
    const deal = DEALS[n.deal];
    const choice = (key: string, label: ReactNode, onClick: () => void, opts: { primary?: boolean; quest?: string; disabled?: boolean; title?: string } = {}) => (
      <Button key={key} small primary={opts.primary} variant={opts.primary ? undefined : 'ghost'} className="fk-talk__choice" disabled={!!busy || opts.disabled} loading={busy === key} title={opts.title ?? (opts.quest ? `About “${opts.quest}”` : undefined)} onClick={onClick} data-testid="talk-choice" data-choice={key}>
        {opts.quest && <span className="fk-talk__quest" aria-hidden="true">❖</span>}{label}
      </Button>
    );
    if (talk.at === 'trouble' && alarmOffer) {
      // docs/120 VM6: anyone in a band takes it up here; band-mates are asked to come along (a band-mate
      // who is not the lead sets out at the head of a fresh band)
      const askedAlong = members.some((m) => myBand && m.partyId === myBand.id && identityHex(m.owner) !== me);
      // (with band-mates to ask, who comes is only known once they answer; the module checks then)
      const why = !myBand ? 'you have no band to take it up with' : party && !askedAlong ? reason(alarmOffer) : undefined;
      choices.push(choice('take', 'We will deal with it', () => void (async () => {
        setBusy('take');
        const r = await rpc.takeUpOffer(alarmOffer.id);
        setBusy(undefined);
        if (r.ok) { playSfx('inn.cheers', { gain: 0.7 }); push(askedAlong ? `You take up “${alarmOffer.title}” and call your band-mates to come.` : `You take up “${alarmOffer.title}”.`, 'success'); endTalk(); }
      })(), { primary: true, disabled: !!why, title: why ? `${why[0]!.toUpperCase()}${why.slice(1)}.` : askedAlong ? `Take up “${alarmOffer.title}”; your band-mates choose whether to come` : `Take up “${alarmOffer.title}”` }));
      choices.push(choice('not-now', 'Not now', () => setTalk({ at: 'greet' })));
    } else if (talk.at !== 'bye') {
      if (talk.at === 'topic') {
        const q = n.topics[talk.i];
        if (q && !talk.follow && q.followAsk) choices.push(choice(`follow-${talk.i}`, q.followAsk, () => setTalk({ at: 'topic', i: talk.i, follow: true }), { quest: q.about || undefined }));
      }
      n.topics.forEach((q, i) => {
        if (talk.at === 'topic' && talk.i === i) return;
        choices.push(choice(`topic-${i}`, q.ask, () => setTalk({ at: 'topic', i, follow: false }), { quest: q.about || undefined }));
      });
      if (alarmOffer && v.alarmNpc === n.id) choices.push(choice('trouble', 'What is the trouble?', () => setTalk({ at: 'trouble' }), { primary: true }));
      if (deal === 'hire' && talk.at !== 'deal') choices.push(choice('deal', <>Would you join us? <Badge tone="gold">{hirePrice(level)}g</Badge></>, () => setTalk({ at: 'deal' }), { title: `Taking ${n.name.split(' ')[0]} on costs ${hirePrice(level)} gold` }));
      if (deal === 'trade' && talk.at !== 'deal' && n.state !== NPC_SOLD) choices.push(choice('deal', <>What are you selling? <Badge tone="gold">{n.price}g</Badge></>, () => setTalk({ at: 'deal' })));
      if (talk.at === 'deal' && deal === 'hire') {
        const price = hirePrice(level);
        choices.push(choice('hire', <>Take them on <Badge tone="gold">{price}g</Badge></>, () => void (async () => {
          if (await pay('hire', price, (h) => rpc.hireVillager(n.id, h))) { push(`${n.name} joins your band.`, 'success'); playSfx('inn.cheers', { gain: 0.5, delayMs: 200 }); setTalk({ at: 'said', line: n.dealYes }); }
        })(), { primary: true }));
        choices.push(choice('no', 'Not today', () => setTalk({ at: 'said', line: n.dealNo })));
      }
      if (talk.at === 'deal' && deal === 'trade') {
        choices.push(choice('trade', <>Buy {n.goods} <Badge tone="gold">{n.price}g</Badge></>, () => void (async () => {
          if (await pay('trade', n.price, (h) => rpc.buyTrade(n.id, h))) { playSfx('ui.item', { gain: 0.7, delayMs: 250 }); push(`${payer?.name} buys ${n.goods}.`, 'success'); setTalk({ at: 'said', line: n.dealYes }); }
        })(), { primary: true }));
        choices.push(choice('no', 'Not today', () => setTalk({ at: 'said', line: n.dealNo })));
      }
      if (deal === 'secret') {
        const told = toldSecret(n);
        if (told) { if (!(talk.at === 'said' && talk.line === told.text)) choices.push(choice('secret-again', 'Tell me again what you know', () => setTalk({ at: 'said', line: told.text }), { quest: told.about || undefined })); }
        else if (talk.at !== 'secret') choices.push(choice('secret', <>You know something. <Badge tone="gold">{n.price}g</Badge></>, () => setTalk({ at: 'secret' }), { quest: n.secretAbout || undefined }));
        else choices.push(choice('buy-secret', <>Pay to hear it <Badge tone="gold">{n.price}g</Badge></>, () => void (async () => {
          if (await pay('buy-secret', n.price, (h) => rpc.buySecret(n.id, h))) setHeard(true);
        })(), { primary: true }));
      }
      if (n.pours) choices.push(choice('round', <>Stand the room a round <Badge tone="gold">{ROUND_PRICE}g</Badge></>, () => void (async () => {
        if (await pay('round', ROUND_PRICE, (h) => rpc.standRound(v.id, h))) { playSfx('inn.cheers'); setHeard(true); }
      })()));
      // docs/64 VP3: the keeper opens the counter, here or in their shop
      // docs/72: the innkeeper and the priest keep a counter too, when their building has stock
      const keeps = (Object.entries(KEEPER_AT) as Array<[VillageSpot, number]>).find(([, role]) => role === n.role)?.[0];
      if (keeps && (n.role === ROLE_MERCHANT || n.role === ROLE_SMITH || stock.some((st) => SPOTS[st.building] === keeps))) choices.push(choice('wares', 'Show me your wares', () => { if (spot !== keeps) setSpot(keeps); endTalk(); setCounterOpen(true); playSfx('ui.open'); }, { primary: spot !== 'street' }));
      // docs/64 VP4: the innkeeper sits the band down to a meal
      if (n.role === ROLE_INNKEEPER && spot === 'inn' && !meal && myHeroes.length > 0) {
        const price = MEAL_PRICE * myHeroes.length;
        choices.push(choice('meal', <>Sit us down for a meal <Badge tone="gold">{price}g</Badge></>, () => void (async () => {
          const diners = heroes.filter((h) => h.mine);
          if (await pay('meal', price, (h) => rpc.orderMeal(v.id, h, myHeroes.map((x) => x.id!)))) {
            endTalk();
            setMeal((m) => ({ n: (m?.n ?? 0) + 1, diners: diners.map((d) => d.key) }));
          }
        })(), { primary: true, title: `${MEAL_PRICE} gold a head for ${myHeroes.length}: a hot meal leaves each hero well fed, +3 max HP in their next fight` }));
      }
      choices.push(choice('bye', 'Farewell', () => setTalk({ at: 'bye' })));
    } else {
      choices.push(choice('leave', 'Walk on', endTalk, { primary: true }));
    }
  }

  const troublePitch = alarmOffer ? alarmOffer.pitch.replace('{asker}', alarmOffer.asker) : '';
  // docs/159 SB3: the basics first, then the week's goods by price
  const counter = COUNTER_SPOTS.includes(spot) ? stock.filter((s) => SPOTS[s.building] === spot).sort((a, b) => Number(alwaysStocked(b)) - Number(alwaysStocked(a)) || a.price - b.price || (a.id < b.id ? -1 : 1)) : [];
  const bandIds = new Set(heroes.filter((h) => h.mine && !h.companion).map((h) => h.key));
  const sellable: CounterItem[] = inventory.filter((i) => bandIds.has(i.heroId.toString()) && i.worldId === worldId).sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((i) => ({ id: i.id, def: i.def, tier: i.tier, name: i.name, owner: mine.find((h) => h.id === i.heroId)?.name ?? 'someone', relic: i.relic, worn: i.equippedSlot !== undefined && i.equippedSlot !== null, upgrades: upsOf(i.id), refund: Math.floor(spentOf(i.id) / 4) }));
  const sell = async (i: CounterItem) => {
    const key = `sell-${i.id}`;
    setBusy(key);
    const r = await rpc.sellItem(i.id);
    setBusy(undefined);
    if (r.ok) { playSfx(i.tier >= 3 ? 'ui.coins_big' : 'ui.trade'); push(`${i.owner} sells the ${i.name.toLowerCase()} for ${sellValue(i.def, i.tier) + (i.refund ?? 0)} gold.`, 'success'); }
  };
  // docs/73 TP5: the shop sells bigger packs (docs/71 INV5), each paid from its own hero's purse
  const packsHere: CounterPack[] = spot !== 'shop' ? [] : myHeroes.flatMap((x) => {
    const h = mine.find((m) => m.id === x.id);
    if (!h) return [];
    const grade = Math.min(PACKS.length - 1, packs.find((p) => p.heroId === h.id)?.grade ?? 0);
    const next = PACKS[grade + 1];
    return [{ heroId: h.id, hero: h.name, gold: h.gold, now: PACKS[grade]!.name, nowCapacity: PACKS[grade]!.capacity, ...(next ? { next: { name: next.name, capacity: next.capacity, price: next.price } } : {}) }];
  });
  const buyPack = async (p: CounterPack) => {
    if (!p.next) return;
    setBusy(`pack-${p.heroId}`);
    const r = await rpc.buyPack(p.heroId);
    setBusy(undefined);
    if (r.ok) { playSfx(p.next.price >= 100 ? 'ui.coins_big' : 'ui.coins'); push(`${p.hero} shoulders a ${p.next.name.toLowerCase()}: room for ${p.next.capacity}.`, 'success'); }
  };
  const fedHere = meals.some((m) => m.villageId === v.id && m.eaten && myHeroes.some((h) => h.id === m.heroId));
  const keeper = npcs.find((n) => KEEPER_AT[spot] === n.role && SPOTS[n.building] === spot) ?? npcs.find((n) => KEEPER_AT[spot] === n.role);
  // the inn and the temple open a counter only when they have something on it
  const hasCounter = spot === 'shop' || spot === 'smithy' || ((spot === 'inn' || spot === 'temple') && counter.length > 0);
  const alarmNpc = npcs.find((n) => n.id === v.alarmNpc);
  // docs/112: the workshop (or the smithy's or temple's upgrade counter) in the room the band stands in
  const hereWorkshop = spot === 'street' ? undefined : workshops.find((w) => w.shop === spot);
  const openWorkshop = workshops.find((w) => w.id === workshopOpen);
  // docs/121 VW3: the meals ordered here, mine and everyone's, so every view serves every band
  const mealsHere = meals.filter((m) => m.villageId === v.id).map((m) => ({ heroKey: m.heroId.toString(), atMs: tsToMs(m.orderedAt) ?? 0 }));

  return (
    <main className="fk-village-screen" data-testid="village-screen" data-ready="true" data-village={v.id.toString()} data-spot={spot}>
      <VillageView look={look} spot={spot} npcs={viewNpcs} heroes={heroes} talking={talking?.id.toString()}
        onTalk={startTalk}
        onEnter={(k) => { resetScene(); setSpot(k); }}
        onLeave={() => { resetScene(); setSpot('street'); }}
        {...(summon ? { summon } : {})} {...(meal ? { meal } : {})} onMeal={onMealStage} onStand={onStand} onHero={openHeroSheet} {...(v.inn ? { inn } : {})}
        me={me} remote={remote} held={held} meals={mealsHere} {...(cameFrom ? { cameFrom } : {})}
        onFigures={(figs, talkNpc) => void rpc.villageMove(v.id, spotN, figs, talkNpc && /^\d+$/.test(talkNpc) ? BigInt(talkNpc) : 0n)}
        onSit={async (seats) => (await rpc.villageSit(v.id, spotN, seats)).ok}
        onNote={(text) => push(text, 'info')}>
        <header className="fk-talk__bar fk-hit">
          <div className="fk-talk__plate">
            <span className="fk-talk__eyebrow">{v.index === 0 ? 'the haven' : `a ${SIZES[v.size] ?? 'village'}`}</span>
            <span className="fk-talk__name" data-testid="village-name">{v.name}</span>
            <span className="fk-talk__epithet">{spot === 'street' ? v.epithet : spot === 'upstairs' ? `${v.inn}: a room upstairs` : isWorkshop(spot) ? SHOP_TITLES[spot] : [v.inn, v.shop, v.smithy, v.temple][SPOTS.indexOf(spot) - 1]}</span>
          </div>
          {v.talkWriting && <span className="fk-talk__writing" data-testid="village-talk-writing">Word of the new notices is going round…</span>}
          <div className="fk-talk__purse">
            <label className="fk-talk__payer">
              <span>Paying</span>
              <Select value={payer?.id.toString() ?? ''} onChange={(e) => setPayerId(BigInt(e.target.value))} data-testid="village-payer" disabled={!payable.length}>
                {payable.map((h) => <option key={h.id.toString()} value={h.id.toString()}>{h.name} · {h.gold}g</option>)}
              </Select>
            </label>
            <span className="fk-talk__gold" title={`${gold} gold across your heroes`}><OryxIcon name="item_coin_bag" scale={2} />{gold}</span>
            <Button small onClick={() => go('world')} data-testid="village-back" title={leading ? 'Back to the chart; your band-mates in town choose whether to come with you' : 'Back to the chart'}>Leave town</Button>
          </div>
        </header>

        {alarmOffer && alarmNpc && !talking && (
          <div className="fk-talk__alarm fk-hit" role="alert" data-testid="village-alarm">
            <HeroSprite frame={alarmNpc.frame} scale={2} title={alarmNpc.name} />
            <span className="fk-talk__alarm-text"><b>{alarmNpc.name}</b> “{v.alarm}”</span>
            <Button small primary onClick={() => { if (SPOTS[alarmNpc.building] !== spot) { setMeal(undefined); setSpot(SPOTS[alarmNpc.building] ?? 'street'); } callOver(alarmNpc.id); }} data-testid="village-alarm-hear">Hear them out</Button>
          </div>
        )}

        <TownBand worldId={worldId} heroes={inTown(me, mine).slice(0, 6)} />

        {mineHere.length > 0 && (
          <Panel variant="solid" className="fk-talk__heard fk-hit" label="What you have heard here" data-testid="village-heard">
            <span className="fk-talk__heard-title">Heard in {v.name}</span>
            <ul>
              {mineHere.slice(0, 4).map((k) => {
                const who = allNpcs.find((n) => n.id === k.npcId);
                return <li key={k.id.toString()} data-testid="village-heard-row"><b>{k.kind === KNOWN_ROUND ? 'Over a round' : who?.name ?? 'Someone'}:</b> {k.text}{k.about && <span className="fk-talk__about"> ❖ {k.about}</span>}</li>;
              })}
            </ul>
          </Panel>
        )}

        {counterOpen && !talking && hasCounter && (
          <Counter title={[v.inn, v.shop, v.smithy, v.temple][SPOTS.indexOf(spot) - 1] || 'The counter'} keeper={keeper?.name} goods={counter} items={sellable} busy={busy} payer={payer?.name}
            onBuy={(s) => void (async () => { if (await pay(`stock-${s.id}`, s.price, (h) => rpc.buyStock(s.id, h))) { playSfx('ui.item', { gain: 0.7, delayMs: 250 }); push(`${payer?.name} buys the ${s.name.toLowerCase()}.`, 'success'); } })()}
            onSell={(i) => void sell(i)}
            {...(spot === 'shop' ? { packs: packsHere, onPack: (p: CounterPack) => void buyPack(p) } : {})}
            {...(keeper ? { onTalk: () => callOver(keeper.id) } : {})}
            onClose={() => { setCounterOpen(false); playSfx('ui.close'); }} />
        )}
        {!counterOpen && !talking && keeper && hasCounter && !meal && (
          <div className="fk-talk__nudge fk-hit" data-testid="village-counter-nudge">
            <span>{keeper.name} keeps the counter.</span>
            <Button small primary onClick={() => { toCounter.current = keeper.id.toString(); callOver(keeper.id); }} data-testid="village-counter-open">Ask to see the wares</Button>
            {hereWorkshop && !hereWorkshop.keeper && (
              <Button small onClick={() => { setWorkshopOpen(hereWorkshop.id); playSfx('ui.open'); }} data-testid="workshop-open">{isEnchantShop(hereWorkshop) ? 'Ask for a blessing' : 'Ask for an improvement'}</Button>
            )}
          </div>
        )}

        {/* docs/142 DY6: the dyer's counter */}
        {openWorkshop && openWorkshop.shop === 'dyer' && !talking && !counterOpen && (
          <Dyer key={openWorkshop.id.toString()} workshop={openWorkshop} busy={busy}
            heroes={payable.filter((h) => myHeroes.some((x) => x.id === h.id)).map((h) => ({
              id: h.id, name: h.name, gold: h.gold, ...(looks[h.id.toString()] ? { look: looks[h.id.toString()]! } : {}),
              worn: inventory.filter((i) => i.heroId === h.id && i.equippedSlot !== undefined && i.equippedSlot !== null)
                .map((i) => ({ id: i.id, def: i.def, name: i.name, dye: ownDye.get(i.id.toString()) ?? looks[h.id.toString()]?.dye ?? 0 })),
              pack: inventory.filter((i) => i.heroId === h.id && (i.equippedSlot === undefined || i.equippedSlot === null))
                .map((i) => ({ id: i.id, def: i.def, name: i.name, dye: ownDye.get(i.id.toString()) ?? 0 })),
            }))}
            onDye={(heroId, dye, payerId, label) => void (async () => {
              setBusy(`dye-${heroId}-${dye}`);
              const r = await rpc.redyeHero(openWorkshop.id, heroId, dye, payerId);
              setBusy(undefined);
              if (r.ok) { playSfx('item.dye'); playSfx('ui.coins', { gain: 0.5, delayMs: 300 }); push(`${label}.`, 'success'); }
            })()}
            onDyePiece={(itemId, dye, payerId, label) => void (async () => {
              setBusy(`dye-item-${itemId}-${dye}`);
              const r = await rpc.dyeItem(openWorkshop.id, itemId, dye, payerId);
              setBusy(undefined);
              if (r.ok) { playSfx('item.dye'); playSfx('ui.coins', { gain: 0.5, delayMs: 300 }); push(`${label}.`, 'success'); }
            })()}
            onClose={() => { setWorkshopOpen(undefined); playSfx('ui.close'); }} />
        )}
        {/* docs/112 UP7: a workshop's counter, and the smithy's and temple's upgrade work */}
        {openWorkshop && openWorkshop.shop !== 'dyer' && !talking && !counterOpen && (
          <Workshop key={openWorkshop.id.toString()} worldId={worldId} workshop={openWorkshop} busy={busy}
            heroes={payable.filter((h) => myHeroes.some((x) => x.id === h.id)).map((h) => ({ id: h.id, name: h.name, gold: h.gold }))}
            onApply={(itemId, upgradeId, payerId, price, label) => void (async () => {
              const key = `up-${itemId}-${upgradeId}`;
              setBusy(key);
              const r = await rpc.upgradeItem(openWorkshop.id, itemId, upgradeId, payerId);
              setBusy(undefined);
              if (r.ok) { if (isEnchantShop(openWorkshop)) playSfx('item.enchant'); else playSfx('item.improve', { band: IMPROVE_BAND[openWorkshop.shop] ?? 0 }); playSfx(price >= 100 ? 'ui.coins_big' : 'ui.coins', { gain: 0.5 }); push(`${label}.`, 'success'); }
            })()}
            onClose={() => { setWorkshopOpen(undefined); playSfx('ui.close'); }} />
        )}
        {!openWorkshop && !talking && !counterOpen && !meal && hereWorkshop && (hereWorkshop.keeper || !(keeper && hasCounter)) && (
          <div className="fk-talk__nudge fk-hit" data-testid="workshop-nudge">
            <span>{hereWorkshop.keeper ? `${hereWorkshop.keeper} keeps the ${SHOP_TITLES[hereWorkshop.shop].replace(/^The /, '').toLowerCase()}.` : hereWorkshop.shop === 'smithy' ? 'The smith takes on improvements too.' : 'The temple lays blessings on arms and armour.'}</span>
            <Button small primary onClick={() => { if (hereWorkshop.keeper) { toCounter.current = undefined; callOver(`${WS_KEY}${hereWorkshop.id}`); } else { setWorkshopOpen(hereWorkshop.id); playSfx('ui.open'); } }} data-testid="workshop-open">
              {hereWorkshop.shop === 'dyer' ? 'See the dyes' : isEnchantShop(hereWorkshop) ? 'See the enchantments' : 'See the improvements'}
            </Button>
          </div>
        )}

        {meal && (
          <div className="fk-talk__meal fk-hit" role="status" data-testid="village-meal" data-stage={mealStage ?? 'seating'}>
            <span className="fk-talk__meal-text">{mealStage === 'resting' ? (fedHere ? <>Well fed: <b>+3 max HP</b> in each hero's next fight. The band sits back and talks.</> : 'The plates are clean; the band sits back and talks.') : mealStage === 'eating' ? 'The band tucks in…' : mealStage === 'serving' ? 'Here comes the food…' : 'The band finds a table…'}</span>
            <Button small primary={mealStage === 'resting'} onClick={() => { setMeal(undefined); setMealStage(undefined); }} data-testid="village-meal-done">{mealStage === 'resting' ? 'Move on' : 'Leave the table'}</Button>
          </div>
        )}

        {talking && (
          <div className="fk-talk__box" data-testid="village-talk" data-npc={talking.id.toString()} data-at={talk.at}>
            <DialogueBox key={`${talking.id}-${JSON.stringify(talk)}`}
              speaker={<>{talking.name}<span className="fk-talk__speaker-epithet">{talking.epithet}</span></>}
              portrait={<HeroSprite frame={talking.frame} scale={3} title={talking.name} />}
              text={lineOf(talking, talk, troublePitch)}
              aside={<ButtonRow className="fk-talk__choices">{choices}</ButtonRow>}
              data-testid="village-dialogue" />
          </div>
        )}
      </VillageView>
    </main>
  );
}
